import axios from 'axios';
import classNames from 'classnames';
import React, {useEffect, useMemo, useState} from 'react';
import {FormattedMessage} from 'react-intl';
import useSWR from 'swr';
import {DataViewerTooltip} from '../../DataViewer/DataViewerTooltip';
import {ImageCardDetail, ImageCardHorizontal} from '../ImageCard';
import {ImageOverlay} from './Overlay';
import {NoDeployment} from '../NoDeployment';
import {toast} from '../../../base-components/StudioToast';
import {useResizeDetector} from 'react-resize-detector';
import './DetectionResult.scss';

// const imageSize = 500;

const SIDEBAR_IMAGE_WIDTH = 125;
const SIDEBAR_IMAGE_HEIGHT = 125;

export type DetectionResultProps = {
  data: Image[];
  deploymentId: string;
  projectId: string;
  search: string;
  searchEnabled: boolean;
};

export type ImageData = {
  label: string;
  bbox: [number, number, number, number];
  progress: number;
};

export type Image = {
  url: string;
  title: string;
  metadata?: {
    size?: number;
    width?: number;
    height?: number;
  };
  data: ImageData[];
};

export function DetectionResult({
  data,
  deploymentId,
  projectId,
  search = '',
  searchEnabled,
}: DetectionResultProps) {
  const [imageSize, setImageSize] = useState(300);
  const {width: detRefWidth, ref: detectionResultRef} = useResizeDetector();

  const [images, setImages] = React.useState<Image[]>();
  const [selectedImage, setSelectedImage] = React.useState<Image | null>(
    data.length ? data[0] : null
  );
  const [selectedLabelIndex, setSelectedLabelIndex] = React.useState<number | null>(null);
  const sortedData = [...(selectedImage?.data ?? [])].sort(
    (a, b) => b.progress - a.progress
  );
  const {data: imageDimensions} = useSWR<
    | {
        height: number;
        width: number;
        size: number;
      }
    | undefined
  >([selectedImage?.url], async () => {
    if (!selectedImage?.url) {
      return undefined;
    }
    if (selectedImage.url.startsWith('blob')) {
      return {
        height: selectedImage?.metadata?.height || 0,
        width: selectedImage?.metadata?.width || 0,
        size: selectedImage?.metadata?.size || 0,
      };
    }
    const {headers} = await axios.get(selectedImage.url, {responseType: 'blob'});
    const xSizesHeader = headers?.['x-sizes'];
    const imageDetails: {w: number; h: number; s: number}[] = JSON.parse(xSizesHeader);
    return {
      height: imageDetails[0].h,
      width: imageDetails[0].w,
      size: imageDetails[0].s,
    };
  });
  const hasResults = useMemo(() => data && Array.isArray(data) && data.length, [data]);

  useEffect(() => {
    if (!hasResults) {
      toast.error('No images found for this deployment.');
    }
  }, [hasResults]);

  useEffect(() => {
    if (detRefWidth) {
      // if window width is greater than 1200px we set the image size to 65% of the container
      if (window?.innerWidth > 1200) {
        setImageSize(detRefWidth / 1.65);
      } else {
        // else we set the width to 100% (+ margin, therefore dividing by 1.1)
        setImageSize(detRefWidth / 1.1);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detRefWidth]);

  useEffect(() => {
    if (hasResults && projectId && deploymentId) {
      if (searchEnabled) {
        let images = data.filter(image =>
          search.toLowerCase() === ''
            ? true
            : image.data?.some(
                item => item.label.toLowerCase().indexOf(search.toLowerCase()) >= 0
              )
        );
        setImages(images);
        setSelectedImage(images.length > 0 ? images[0] : null);
      } else {
        setImages(data);
        setSelectedImage(data.length > 0 ? data[0] : null);
      }
    }
    return () => {
      setImages(undefined);
      setSelectedImage(null);
    };
  }, [searchEnabled, hasResults, data, projectId, deploymentId, search]);

  return (
    <div className="detection-result">
      {!hasResults ? (
        <NoDeployment
          imageUrl="images/errorGettingData.png"
          message={<FormattedMessage id="test.noImagesFetched" />}
        />
      ) : !images?.length ? (
        <NoDeployment
          imageUrl="images/errorGettingData.png"
          message={<FormattedMessage id="test.noImagesResult" />}
        />
      ) : (
        <>
          <div className="detection-result__sidebar">
            {images &&
              images.map((image, i) => {
                const imageUrl = image.url.startsWith('blob')
                  ? image.url
                  : `${image.url}&resolution=${SIDEBAR_IMAGE_WIDTH}:${SIDEBAR_IMAGE_HEIGHT}`;
                return (
                  <ImageCardHorizontal
                    key={`iamge-card-horizantal-${i}`}
                    className={classNames(
                      'image-card-horizontal',
                      selectedImage?.url === image.url &&
                        'detection-result__image--selected'
                    )}
                    image={imageUrl}
                    width={SIDEBAR_IMAGE_WIDTH}
                    height={SIDEBAR_IMAGE_HEIGHT}
                    data-testid="detection-inference-result"
                    onClick={() => {
                      setSelectedImage(image);
                    }}
                    title={image?.title}
                  />
                );
              })}
          </div>
          <div
            ref={detectionResultRef}
            className={`detection-result__review ${(detRefWidth || 0) < 300 && 'split'}`}
          >
            {selectedImage?.url && (
              <>
                <DataViewerTooltip
                  filename={selectedImage?.title}
                  className="detection-result__image"
                  size={imageDimensions?.size ?? 0}
                  width={imageDimensions?.width}
                  height={imageDimensions?.height}
                  url={selectedImage.url}
                  annotation={sortedData.map(el => ({
                    ...el,
                    bbox: [
                      (el.bbox?.[0] ?? 0) * (imageDimensions?.width || 0),
                      (el.bbox?.[1] ?? 0) * (imageDimensions?.height || 0),
                      (el.bbox?.[2] ?? 0) * (imageDimensions?.width || 0),
                      (el.bbox?.[3] ?? 0) * (imageDimensions?.height || 0),
                    ],
                  }))}
                >
                  <ImageOverlay
                    size={imageSize}
                    width={imageDimensions?.width || 0}
                    height={imageDimensions?.height || 0}
                    image={selectedImage.url}
                    data={sortedData}
                    highlightIndex={selectedLabelIndex}
                    showLabel={true}
                    selectOnMouseOver={true}
                    filename={selectedImage?.title}
                  />
                </DataViewerTooltip>
                <div className="detection-result__stats">
                  {selectedImage.data?.length > 0 && (
                    <ImageCardDetail
                      data={sortedData}
                      onHover={(_stats, index) => {
                        setSelectedLabelIndex(index);
                      }}
                      countObjects={true}
                    />
                  )}
                </div>
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
}
